import { Link, router } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import { useOrgPath } from '@/lib/pathFor'
import { capitalize } from '@/lib/utils'
import { formatTime } from '@/lib/formatters'

interface ClientDetail {
  id: string
  name: string
  address: string | null
  website: string | null
  status: string
  credit_limit: number | null
}

interface Contact {
  id: string
  name: string | null
  email: string
  company: string | null
}

interface ShipmentRow {
  id: string
  origin_port: string | null
  destination_port: string | null
  status: string | null
  commodity: string | null
  bookings_count: number
  earliest_sailing: string | null
  show_url: string
}

interface ThreadRow {
  id: string
  subject: string
  status: string
  latest_date: string | null
  emails_count: number
  show_url: string
}

interface ClientsShowProps {
  client: ClientDetail
  contacts: Contact[]
  shipments: ShipmentRow[]
  email_threads: ThreadRow[]
}

const THREAD_STATUS_COLORS: Record<string, string> = {
  open: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  closed: 'bg-gray-100 text-gray-600',
}

export default function ClientsShow({ client, contacts, shipments, email_threads }: ClientsShowProps) {
  const pathFor = useOrgPath()
  return (
    <>
      <header className="flex h-14 items-center gap-3 border-b px-8">
        <Link href={pathFor('/clients')} className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <h1 className="text-sm font-semibold">{client.name}</h1>
        <Badge variant="secondary" size="sm" className={`font-medium ${client.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
          {capitalize(client.status)}
        </Badge>
        <span className="text-xs text-muted-foreground">
          Credit limit: {client.credit_limit != null ? `$${client.credit_limit.toLocaleString('en-US')}` : '—'}
        </span>
        <div className="ml-auto">
          <Link href={pathFor(`/clients/${client.id}/edit`)}>
            <Button variant="outline" size="sm" className="text-xs">Edit</Button>
          </Link>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-8 space-y-8">
        {/* Company Info */}
        <section className="space-y-2">
          {client.address && <p className="text-[13px] text-muted-foreground">{client.address}</p>}
          {client.website && (
            <a href={client.website} target="_blank" rel="noreferrer" className="text-[13px] text-primary hover:underline">
              {client.website}
            </a>
          )}
        </section>

        {/* Contacts */}
        <section>
          <h2 className="text-[13px] font-semibold text-foreground mb-3">Contacts</h2>
          <div className="rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Email</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="px-4 py-2 text-[13px]">{c.name || '-'}</td>
                    <td className="px-4 py-2 text-[13px] text-muted-foreground">{c.email}</td>
                  </tr>
                ))}
                {contacts.length === 0 && (
                  <tr><td colSpan={2} className="px-4 py-4 text-center text-xs text-muted-foreground">No contacts</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Shipments */}
        <section>
          <h2 className="text-[13px] font-semibold text-foreground mb-3">Shipments</h2>
          <div className="rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Route</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Commodity</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Bookings</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Earliest Sailing</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {shipments.map((s) => (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => router.visit(s.show_url)}>
                    <td className="px-4 py-2 text-[13px] font-medium">
                      {s.origin_port && s.destination_port ? `${s.origin_port} → ${s.destination_port}` : '-'}
                    </td>
                    <td className="px-4 py-2 text-[13px] text-muted-foreground">{s.commodity || '-'}</td>
                    <td className="px-4 py-2 text-[13px] text-muted-foreground">{s.bookings_count}</td>
                    <td className="px-4 py-2 text-[13px] text-muted-foreground">{s.earliest_sailing || '-'}</td>
                    <td className="px-4 py-2">
                      {s.status && (
                        <Badge variant="secondary" size="sm" className="font-medium">
                          {capitalize(s.status)}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
                {shipments.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-4 text-center text-xs text-muted-foreground">No shipments</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Email Threads */}
        <section>
          <h2 className="text-[13px] font-semibold text-foreground mb-3">Email Threads</h2>
          <div className="rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Subject</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Emails</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Last Activity</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {email_threads.map((t) => (
                  <tr key={t.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => router.visit(t.show_url)}>
                    <td className="px-4 py-2 text-[13px] font-medium">{t.subject}</td>
                    <td className="px-4 py-2 text-[13px] text-muted-foreground">{t.emails_count}</td>
                    <td className="px-4 py-2 text-[13px] text-muted-foreground">{t.latest_date ? formatTime(t.latest_date) : '-'}</td>
                    <td className="px-4 py-2">
                      <Badge variant="secondary" size="sm" className={`font-medium ${THREAD_STATUS_COLORS[t.status] || ''}`}>
                        {capitalize(t.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {email_threads.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-4 text-center text-xs text-muted-foreground">No email threads</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  )
}

ClientsShow.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>{page}</AuthenticatedLayout>
)
